// ============================================================================
// FILEUPLOADER.JS - MODAL DE SUBIDA DE ARCHIVOS CON DRAG & DROP - VERSIÓN CORREGIDA
// ============================================================================

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Trash2,
  Eye,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Download,
  Loader2
} from 'lucide-react';
import useToast from '../services/useToast';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../services/apiClient';
import { useHasHover } from './shared/useMediaQuery';
import '../styles/file-uploader.css';

function FileUploader({
  isOpen,
  onClose,
  onUploadSuccess,
  taskId = null,
  // Destino de subida configurable (por defecto, adjuntos de tarea). Otros
  // módulos (p.ej. creatividades) pasan su endpoint/param/id.
  uploadEndpoint = 'upload',
  uploadParam = 'task_id',
  uploadId = null,
  maxFiles = 5,
  maxSizePerFile = 25 * 1024 * 1024,
  // Restricción opcional de tipos: si se omite, vale la whitelist global.
  allowedMimeTypes = null,
  allowedTypesLabel = null
}) {
  // Id destino: uploadId si se pasa, si no el taskId (retrocompat tareas).
  const targetId = uploadId ?? taskId;
  const { showSuccess, showError, confirm } = useToast();
  const { token } = useAuth();

  // Estados del uploader
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [errors, setErrors] = useState([]);

  // Referencias
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  // En táctil no hay arrastre: la zona se presenta como botón «Elegir archivos»
  const hasHover = useHasHover();
  const dropRef = useRef(null);

  // Tipos de archivo permitidos
  const allowedTypes = {
    images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    documents: [
      'application/pdf', 
      'text/plain', 
      'text/csv',
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ],
    others: [
      'application/zip',
      'application/x-zip-compressed',
      'application/x-rar-compressed',
      'application/x-7z-compressed'
    ],
    videos: ['video/mp4', 'video/webm']
  };

  // Whitelist efectiva: la global salvo que el módulo la restrinja (p.ej.
  // creatividades, cuyo backend solo acepta imagen o vídeo).
  const allAllowedTypes = allowedMimeTypes?.length
    ? allowedMimeTypes
    : [...allowedTypes.images, ...allowedTypes.documents, ...allowedTypes.others, ...allowedTypes.videos];

  // Reset al abrir/cerrar modal
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setErrors([]);
      setUploadProgress({});
      setPreviewFile(null);
      setUploading(false);
    }
  }, [isOpen]);

  // Diálogo: foco inicial en la caja al abrir
  useEffect(() => {
    if (isOpen) modalRef.current?.focus();
  }, [isOpen]);

  // Manejar tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !uploading) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, uploading]);

  // Validar archivo individual
  const validateFile = (file) => {
    const errors = [];

    // Validar tipo
    if (!allAllowedTypes.includes(file.type)) {
      errors.push(`Tipo de archivo no permitido: ${file.type}`);
    }

    // Validar tamaño
    if (file.size > maxSizePerFile) {
      const maxSizeMB = Math.round(maxSizePerFile / (1024 * 1024));
      errors.push(`Archivo muy grande. Máximo ${maxSizeMB}MB`);
    }

    // Validar nombre
    if (file.name.length > 100) {
      errors.push('Nombre de archivo muy largo (máx 100 caracteres)');
    }

    return errors;
  };

  // Procesar archivos seleccionados
  const processFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList);
    const validFiles = [];
    const newErrors = [];

    // Verificar límite total
    if (files.length + newFiles.length > maxFiles) {
      newErrors.push(`Máximo ${maxFiles} archivos permitidos`);
      setErrors(newErrors);
      return;
    }

    newFiles.forEach((file, index) => {
      // Verificar si ya existe
      const exists = files.some(f => f.name === file.name && f.size === file.size);
      if (exists) {
        newErrors.push(`Archivo duplicado: ${file.name}`);
        return;
      }

      // Validar archivo
      const fileErrors = validateFile(file);
      if (fileErrors.length > 0) {
        newErrors.push(`${file.name}: ${fileErrors.join(', ')}`);
        return;
      }

      // Crear wrapper manteniendo referencia al File original
      const fileWithId = {
        originalFile: file, // Referencia al File nativo
        id: `${Date.now()}-${index}`,
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        preview: null,
        status: 'pending'
      };

      // Generar preview para imágenes
      if (allowedTypes.images.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileWithId.preview = e.target.result;
          setFiles(prev => prev.map(f => f.id === fileWithId.id ? { ...f, preview: e.target.result } : f));
        };
        reader.readAsDataURL(file);
      }

      validFiles.push(fileWithId);
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
    } else {
      setErrors([]);
    }

    setFiles(prev => [...prev, ...validFiles]);
  }, [files, maxFiles, maxSizePerFile, allowedTypes.images]);

  // Manejar drag & drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  // Manejar selección manual
  const handleFileSelect = (e) => {
    if (e.target.files) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  // Eliminar archivo de la lista
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  // FUNCIÓN CRÍTICA: Subir archivo al servidor
  const uploadToServer = async (fileWrapper) => {
    if (!targetId) {
      throw new Error('Falta el destino para subir archivos');
    }

    const formData = new FormData();
    formData.append('file', fileWrapper.originalFile);

    // Subida multipart: el apiClient NO fija Content-Type (boundary automático),
    // pone el Bearer y deriva la base por tenant.
    const result = await apiClient.upload(
      `${uploadEndpoint}&${uploadParam}=${targetId}`,
      formData,
      { token }
    );

    if (result.status !== 'success') {
      throw new Error(result.message || 'Error al subir archivo');
    }

    return result.data;
  };

  // Subir todos los archivos
  const handleUploadAll = async () => {
    if (!targetId) {
      showError('No se ha especificado el destino de los archivos');
      return;
    }

    if (files.length === 0) return;

    console.log('🚀 Iniciando subida masiva de archivos...');
    setUploading(true);
    setErrors([]);

    const uploadedFiles = [];
    const failedFiles = [];

    // Procesar archivos secuencialmente
    for (const file of files) {
      if (file.status === 'success') continue;

      try {
        // Actualizar estado a subiendo
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'uploading' } : f
        ));

        setUploadProgress(prev => ({ ...prev, [file.id]: 50 }));

        console.log(`📤 Subiendo: ${file.name}`);
        const result = await uploadToServer(file);

        // Marcar como exitoso
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'success', serverData: result } : f
        ));
        setUploadProgress(prev => ({ ...prev, [file.id]: 100 }));

        uploadedFiles.push({
          originalFile: file,
          serverData: result
        });

        console.log(`✅ Subido exitosamente: ${file.name}`);

      } catch (error) {
        console.error(`❌ Error subiendo ${file.name}:`, error);
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', errorMessage: error.message } : f
        ));

        failedFiles.push({
          file,
          error: error.message
        });
      }
    }

    setUploading(false);

    // Mostrar resultados
    if (uploadedFiles.length > 0) {
      console.log(`✅ ${uploadedFiles.length} archivos subidos exitosamente`);
      
      showSuccess(
        `${uploadedFiles.length} archivo${uploadedFiles.length !== 1 ? 's' : ''} subido${uploadedFiles.length !== 1 ? 's' : ''} correctamente`
      );
      
      if (onUploadSuccess) {
        onUploadSuccess(uploadedFiles);
      }
      
      setTimeout(() => {
        handleClose();
      }, 1500);
    }

    if (failedFiles.length > 0) {
      console.error(`❌ ${failedFiles.length} archivos fallaron`);
      const errorMessages = failedFiles.map(f => `${f.file.name}: ${f.error}`);
      setErrors(errorMessages);
      showError(`Error al subir ${failedFiles.length} archivo${failedFiles.length !== 1 ? 's' : ''}`);
    }
  };

  // Cerrar modal
  const handleClose = async () => {
    if (uploading) {
      const userConfirmed = await confirm(
        'Hay archivos subiendo. ¿Estás seguro de que quieres cerrar?',
        {
          title: 'Confirmación',
          confirmText: 'Cerrar de todos modos',
          cancelText: 'Continuar subiendo'
        }
      );
      
      if (!userConfirmed) return;
    }
    
    onClose();
  };

  // Obtener icono según tipo de archivo
  const getFileIcon = (file) => {
    if (!file || !file.type) {
      return <FileIcon size={20} className="file-icon default" />;
    }
    
    const fileType = file.type.toLowerCase();
    
    // Imágenes
    if (fileType.startsWith('image/')) {
      return <ImageIcon size={20} className="file-icon image" />;
    }
    
    // PDF
    if (fileType === 'application/pdf') {
      return <FileIcon size={20} className="file-icon pdf" />;
    }
    
    // Excel
    if (fileType === 'application/vnd.ms-excel' || 
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      return <FileText size={20} className="file-icon excel" />;
    }
    
    // Word
    if (fileType === 'application/msword' || 
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <FileText size={20} className="file-icon word" />;
    }
    
    // CSV
    if (fileType === 'text/csv') {
      return <FileText size={20} className="file-icon csv" />;
    }
    
    // PowerPoint
    if (fileType === 'application/vnd.ms-powerpoint' || 
        fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
      return <FileText size={20} className="file-icon powerpoint" />;
    }
    
    // Archivos comprimidos
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('7z')) {
      return <FileIcon size={20} className="file-icon compressed" />;
    }
    
    // Texto plano
    if (fileType === 'text/plain') {
      return <FileText size={20} className="file-icon text" />;
    }
    
    return <FileIcon size={20} className="file-icon default" />;
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Extensión corta para el badge del archivo
  const getFileExtension = (name) => {
    if (!name || !name.includes('.')) return '';
    return name.split('.').pop().slice(0, 4).toUpperCase();
  };

  // Componente de archivo individual
  const FileItem = ({ file }) => {
    const progress = uploadProgress[file.id] || 0;
    const ext = getFileExtension(file.name);

    return (
      <div className={`file-item ${file.status || 'pending'}`}>
        <div className="file-badge">
          {file.preview ? (
            <img
              src={file.preview}
              alt={file.name}
              className="file-preview-thumb"
              onClick={() => setPreviewFile(file)}
            />
          ) : (
            <>
              {getFileIcon(file)}
              {ext && <span className="file-badge-ext">{ext}</span>}
            </>
          )}
        </div>

        <div className="file-details">
          <div className="file-name" title={file.name}>
            {file.name}
          </div>
          <div className="file-meta">
            <span className="file-size">{formatFileSize(file.size)}</span>
            <span className="meta-dot">•</span>
            {file.status === 'pending' && (
              <span className="status-pending">Listo para subir</span>
            )}
            {file.status === 'uploading' && (
              <span className="status-uploading">
                <Loader2 size={13} className="spinning" />
                Subiendo… {progress}%
              </span>
            )}
            {file.status === 'success' && (
              <span className="status-success">
                <CheckCircle size={13} />
                Completado
              </span>
            )}
            {file.status === 'error' && (
              <span className="status-error">
                <AlertCircle size={13} />
                {file.errorMessage || 'Error desconocido'}
              </span>
            )}
          </div>

          {file.status === 'uploading' && (
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>

        <div className="file-actions">
          {file.preview && (
            <button
              className="file-action-btn preview"
              onClick={() => setPreviewFile(file)}
              title="Ver preview"
            >
              <Eye size={16} />
            </button>
          )}

          {file.status !== 'uploading' && (
            <button
              className="file-action-btn remove"
              onClick={() => removeFile(file.id)}
              title="Eliminar archivo"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Modal de vista previa
  const PreviewModal = () => {
    if (!previewFile) return null;
    
    return (
      <div className="preview-modal-overlay" onClick={() => setPreviewFile(null)}>
        <div className="preview-modal" onClick={e => e.stopPropagation()}>
          <div className="preview-header">
            <h4>{previewFile.name}</h4>
            <button className="preview-close" onClick={() => setPreviewFile(null)}>
              <X size={20} />
            </button>
          </div>
          <div className="preview-content">
            <img src={previewFile.preview} alt={previewFile.name} />
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="file-uploader-overlay u-sheet-overlay">
        <div
          className="file-uploader-modal u-sheet"
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="file-uploader-title"
          tabIndex={-1}
        >
          <div className="modal-header">
            <div className="header-title">
              <div className="header-icon">
                <Upload size={20} strokeWidth={2.2} />
              </div>
              <div className="header-text">
                <h2 id="file-uploader-title">
                  Subir archivos
                  <span className="file-count">{files.length}/{maxFiles}</span>
                </h2>
                <p>Selecciona y sube los archivos que quieras adjuntar</p>
              </div>
            </div>
            <button type="button" className="close-button" onClick={handleClose} aria-label="Cerrar">
              <X size={20} />
            </button>
          </div>

          <div 
            ref={dropRef}
            className={`drop-zone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              {dragActive ? (
                <>
                  <div className="drop-zone-icon">
                    <Download size={32} />
                  </div>
                  <p className="drop-active">Suelta los archivos aquí</p>
                </>
              ) : (
                <>
                  <span className="drop-upload-btn">
                    <Upload size={15} strokeWidth={2.2} />
                    {hasHover ? 'Subir' : 'Elegir archivos'}
                  </span>
                  <div className="drop-zone-text">
                    <p className="drop-main">
                      {hasHover ? 'Sube un archivo o arrástralo aquí' : 'Toca para elegir archivos del dispositivo'}
                    </p>
                    <p className="drop-hint">
                      Máximo {maxFiles} archivos · {Math.round(maxSizePerFile / (1024 * 1024))} MB por archivo
                    </p>
                    <p className="drop-types">
                      {allowedTypesLabel || 'Imágenes, PDF, Word, Excel, CSV, ZIP, PowerPoint o vídeo MP4'}
                    </p>
                  </div>
                </>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={allAllowedTypes.join(',')}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {errors.length > 0 && (
            <div className="errors-section">
              <div className="errors-header">
                <AlertCircle size={16} />
                <span>Errores encontrados:</span>
              </div>
              <ul className="errors-list">
                {errors.map((error, index) => (
                  <li key={index} className="error-item">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {files.length > 0 && (
            <div className="files-section">
              <div className="files-header">
                <span className="files-count">
                  {files.length} archivo{files.length !== 1 ? 's' : ''} seleccionado{files.length !== 1 ? 's' : ''}
                </span>

                {files.length > 1 && !uploading && (
                  <button
                    className="clear-all-btn"
                    onClick={() => setFiles([])}
                  >
                    Limpiar todo
                  </button>
                )}
              </div>

              <div className="files-list">
                {files.map(file => (
                  <FileItem key={file.id} file={file} />
                ))}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <div className="footer-info">
              {uploading && (
                <div className="upload-status">
                  <RefreshCw size={16} className="spinning" />
                  <span>Subiendo archivos...</span>
                </div>
              )}
            </div>
            
            <div className="footer-actions">
              <button 
                className="footer-button secondary"
                onClick={handleClose}
                disabled={uploading}
              >
                {uploading ? 'Subiendo...' : 'Cancelar'}
              </button>
              
              <button 
                className="footer-button primary"
                onClick={handleUploadAll}
                disabled={files.length === 0 || uploading || files.some(f => f.status === 'error')}
              >
                {uploading ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Subir {files.length} archivo{files.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal />
    </>
  );
}

export default FileUploader;